import { isNil } from 'lodash';

import { getDecimalSafeValue } from 'utils/mathUtils';
import { FinancialInfoFormValues } from './FinancialInfo';
import {
  PriceAgreementW2InfoResponseModel,
  AssignmentTypeEnum,
  BOUsersPriceAgreementRespModel,
} from 'api/backofficeApiClient';
import { WorkerInfoFormValues } from '../AssignmentModal';
import { WorksiteLocationFormValues } from '../WorksiteLocation';
import { FinancialInfoSchedulerModel } from 'components/modals/FinancialInfoScheduleModal';

export const OVERTIME_MULTIPLIER = 1.5;
export const DT_PAY_RATE_MULTIPLIER = 2;

export type MspVmsValuesType = {
  vmsRate?: number | null;
  mspRate?: number | null;
};

// Every math operation should be wrapped in getDecimalSafeValue
// due to inexact floats of js calculations
export class FinancialInfoCalculator {
  public static getBillRateValue = (billRate?: number | null): number =>
    billRate || 0;

  public static getPayRateValue = (payRate?: number | null): number =>
    payRate || 0;

  public static getProfitRateValue = (
    values?: FinancialInfoFormValues | FinancialInfoSchedulerModel,
    mspVmsValues?: MspVmsValuesType,
  ): number => {
    const mbpRate = values?.mbpRate || 0;
    const billRate = FinancialInfoCalculator.getBillRateValue(values?.billRate);
    const payRate = FinancialInfoCalculator.getPayRateValue(values?.payRate);
    const vmsPortion = FinancialInfoCalculator.getVmsPortionValue(
      billRate,
      mspVmsValues?.vmsRate,
    );
    const mspPortion = FinancialInfoCalculator.getMspPortionValue(
      billRate,
      mspVmsValues?.mspRate,
    );
    const mbpPortion = FinancialInfoCalculator.getMbpPortionValue(
      payRate,
      mbpRate,
    );

    return getDecimalSafeValue(
      billRate - (payRate + vmsPortion + mspPortion + mbpPortion),
    );
  };
  public static getMinimumBillRateForZeroProfit = (
    values?: FinancialInfoFormValues | FinancialInfoSchedulerModel,
    mspVmsValues?: MspVmsValuesType,
  ): number => {
    const mbpRate = values?.mbpRate || 0;
    const payRate = FinancialInfoCalculator.getPayRateValue(values?.payRate);
    const vmsRate = mspVmsValues?.vmsRate || 0;
    const mspRate = mspVmsValues?.mspRate || 0;

    // Formula: Bill Rate = Pay Rate * (1 + MBP Rate / 100) / (1 - (VMS Rate + MSP Rate) / 100)
    // Converting to integer math: Bill Rate = Pay Rate * (100 + MBP Rate) / (100 - VMS Rate - MSP Rate)
    const numerator = Math.round(payRate * (10000 + Math.round(mbpRate * 100)));
    const denominator =
      10000 - Math.round(vmsRate * 100) - Math.round(mspRate * 100);

    if (denominator === 0) {
      return 0;
    }

    const minimumBillRate = numerator / denominator;

    return getDecimalSafeValue(minimumBillRate);
  };

  public static getVmsPortionValue = (
    billRate?: number | null,
    vmsRate?: number | null,
  ): number => {
    const vmsPortion = getDecimalSafeValue(
      getDecimalSafeValue((billRate || 0) * (vmsRate || 0)) / 100,
    );

    return vmsPortion;
  };

  public static getMspPortionValue = (
    billRate?: number | null,
    mspRate?: number | null,
  ): number => {
    const mspPortion = getDecimalSafeValue(
      getDecimalSafeValue((billRate || 0) * (mspRate || 0)) / 100,
    );
    return mspPortion;
  };

  public static getMbpPortionValue = (
    payRateValue?: number | null,
    mbpRateValue?: number | null,
  ): number => {
    const payRate = FinancialInfoCalculator.getPayRateValue(payRateValue);
    const mbpRate = mbpRateValue || 0;
    return getDecimalSafeValue(getDecimalSafeValue(payRate * mbpRate) / 100);
  };

  public static getMarginPercentValue = (
    values?: FinancialInfoFormValues,
    mspVmsValues?: MspVmsValuesType,
  ): number => {
    const billRate = FinancialInfoCalculator.getBillRateValue(values?.billRate);

    if (billRate === 0) return 0;

    const profitRate = FinancialInfoCalculator.getProfitRateValue(
      values,
      mspVmsValues,
    );

    return getDecimalSafeValue(
      getDecimalSafeValue(profitRate / billRate) * 100,
    );
  };

  public static getMarkupPercentValue = (
    values?: FinancialInfoFormValues,
  ): number => {
    const billRate = FinancialInfoCalculator.getBillRateValue(values?.billRate);
    const payRate = FinancialInfoCalculator.getPayRateValue(values?.payRate);

    return getDecimalSafeValue(
      getDecimalSafeValue((billRate - payRate) / payRate) * 100,
    );
  };
  public static getInitialMbpRate = (
    workerInfo?: WorkerInfoFormValues,
    worksiteLocation?: WorksiteLocationFormValues,
    assignmentType?: AssignmentTypeEnum | null,
    priceAgreement?: BOUsersPriceAgreementRespModel,
  ): number => {
    const w2InfoList = priceAgreement?.w2InfoList;
    const isW2Assignment = assignmentType === AssignmentTypeEnum.EEAssignment;

    if (!isW2Assignment) {
      return priceAgreement?.icPrice || 0;
    }

    const w2InfoByWorkSiteWorkLocation =
      FinancialInfoCalculator.w2InfoByWorkSiteWorkLocation(
        w2InfoList,
        isW2Assignment,
        workerInfo,
        worksiteLocation,
      );

    return w2InfoByWorkSiteWorkLocation
      ? (w2InfoByWorkSiteWorkLocation.w2Price as number)
      : 0;
  };
  public static getWorkerLocation = (
    isW2Assignment: boolean,
    workerInfo?: WorkerInfoFormValues,
  ) => {
    const worker = workerInfo?.worker;
    const company = workerInfo?.company;

    return isW2Assignment
      ? {
          country: worker?.country || undefined,
          internationalStateName: worker?.internationalStateName || undefined,
          state: worker?.state,
          internationalCityName: worker?.internationalCityName || undefined,
          city: worker?.city || undefined,
          address: worker?.address || undefined,
          zip: worker?.zip || undefined,
        }
      : {
          country: company?.country || undefined,
          state: company?.state || undefined,
          internationalStateName: company?.internationalStateName || undefined,
          city: company?.city || undefined,
          internationalCityName: company?.internationalCityName || undefined,
          address: company?.address || undefined,
          zip: company?.zip || undefined,
        };
  };
  public static getWorksiteWorkLocation = (
    isW2Assignment: boolean,
    workerInfo?: WorkerInfoFormValues,
    worksiteLocation?: WorksiteLocationFormValues,
  ) => {
    const work = worksiteLocation?.work;
    return work?.locationSameAsMain
      ? FinancialInfoCalculator.getWorkerLocation(isW2Assignment, workerInfo)
      : {
          state: work?.state,
          city: work?.city,
          address: work?.address,
          zip: work?.zip,
        };
  };
  public static w2InfoByWorkSiteWorkLocation = (
    w2InfoList: PriceAgreementW2InfoResponseModel[] | undefined,
    isW2Assignment: boolean,
    workerInfo?: WorkerInfoFormValues,
    worksiteLocation?: WorksiteLocationFormValues,
  ) => {
    const workSiteWorkLocation =
      FinancialInfoCalculator.getWorksiteWorkLocation(
        isW2Assignment,
        workerInfo,
        worksiteLocation,
      );

    if (
      !w2InfoList?.length ||
      !workSiteWorkLocation?.state ||
      !isW2Assignment
    ) {
      return;
    }

    const w2InfoStateIds = w2InfoList.map((w2Info) => ({
      ...w2Info,
      placementStates: (w2Info.placementStates || []).map((state) => state.id),
    }));

    return w2InfoStateIds.find((w2Info) =>
      w2Info.placementStates.includes(workSiteWorkLocation.state),
    );
  };
  static getMBPRateValue = (
    isW2Assignment?: boolean,
    icPrice?: number,
    w2Price?: number,
  ) => {
    if (!isW2Assignment) return icPrice || 0;

    return w2Price || 0;
  };
  public static calculatePayRatePTOValue = (
    basePayRate?: number | null,
  ): number => basePayRate || 0;
  public static calculateBillRatePTOValue = (
    billRate?: number | null,
    mbpPortion?: number | null,
    isPaidByMember?: boolean,
  ): number | null => {
    if (isNil(isPaidByMember)) return null;

    return isPaidByMember ? (billRate || 0) + (mbpPortion || 0) : billRate || 0;
  };
}
